import { CompanyCategory, Feed } from 'src/app/core/models/core.models';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  take,
  tap,
} from 'rxjs';
import { selectActiveWorkspaceId } from 'src/app/core/reducers';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';
import {
  injectWidgetPresetsQuery,
  WidgetPresetsScope,
} from 'src/app/api/queries/widget.queries';
import {
  injectFeedUserCategoriesQuery,
  injectWorkflowsQuery,
} from 'src/app/api/queries/feed.queries';

import { AppState } from 'src/app/reducers';
import { FormMode, FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from '../../../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { NomLanguages, NomObjectClass } from 'src/app/core/constants';
import { NomObjectTypes } from './../../../../constants';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { WidgetPreset, WidgetVizType } from 'src/app/core/models/widget.models';

// Shape stored in the `widget_selections` form control. Each entry represents
// a preset the user picked plus which of its viz-type variants they want
// included. Flows into the createFeed payload as `widget_selections: WidgetSelection[]`.
export interface WidgetSelection {
  preset_id: number;
  viz_type_ids: number[];
}

@Component({
  selector: 'app-query-editor-general-settings',
  templateUrl: './query-editor-general-settings.component.html',
  styleUrls: ['./query-editor-general-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QueryEditorGeneralSettingsComponent
  extends GeneralDynamicFormComponent
  implements AfterViewInit
{
  @Input() feed: Feed;
  @Input() objectTypeId: number;
  @Output() componentDataChange = new EventEmitter<{
    type: string;
    value: any;
  }>();
  availableCategories: CompanyCategory[] = [];

  private elementRef = inject(ElementRef<HTMLElement>);

  // ! Starter-widget gallery — surfaced only when creating a new feed.
  // ! Selections are stored in the `widget_selections` form control as
  // ! `WidgetSelection[]` and flow through `tabManager.combineValues()` into
  // ! the `createFeed` action and on to the PUT /feed endpoint.
  // Set where the gallery is initialised - it needs the @Input objectTypeId.
  private readonly presetsScope = signal<WidgetPresetsScope | null>(null);
  private readonly widgetPresetsQuery = injectWidgetPresetsQuery(
    this.presetsScope,
  );

  // Declared above `formProperties` on purpose: that array is a field initializer
  // and reads these streams while building the controls.
  private readonly categoriesFeedId = signal<string | null>(null);
  private readonly feedUserCategoriesQuery = injectFeedUserCategoriesQuery(
    this.categoriesFeedId,
  );
  private readonly workflowsQuery = injectWorkflowsQuery();
  private readonly userWorkspaces = inject(UserWorkspacesService);

  private readonly availableCategories$ = toObservable(
    computed(() => this.feedUserCategoriesQuery.data() ?? []),
  );
  private readonly workflows$ = toObservable(
    computed(() => this.workflowsQuery.data() ?? []),
  );

  widgetPresets$: Observable<WidgetPreset[]> = toObservable(
    computed(() => this.widgetPresetsQuery.data() ?? []),
  );
  filteredWidgetPresets$: Observable<WidgetPreset[]>;
  widgetPresetsLoading$: Observable<boolean> = toObservable(
    computed(() => this.widgetPresetsQuery.isLoading()),
  );
  widgetPresetSearch$ = new BehaviorSubject<string>('');
  private static readonly WIDGET_PRESETS_CONTROL = 'widget_selections';

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    private translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  public activeFormProperties: FormProperty[] = [];
  public formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.DEFAULT]: 'title',
        [NomLanguages.BULGARIAN]:
          'title_i18n.language_id_' + NomLanguages.BULGARIAN,
        [NomLanguages.ENGLISH]:
          'title_i18n.language_id_' + NomLanguages.ENGLISH,
        [NomLanguages.ARABIC]: 'title_i18n.language_id_' + NomLanguages.ARABIC,
      },
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.title',
      ),
      controlType: 'text',
      validators: [Validators.required],
      additionalConfig: {
        propertyLabel: this.translateService.instant(
          'query-editor-general.placeholder.title',
        ),
      },
    },
    {
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.description',
      ),
      controlType: 'textarea',
      additionalConfig: {
        propertyLabel: this.translateService.instant(
          'query-editor-general.placeholder.description',
        ),
        placeholder: this.translateService.instant(
          'query-editor-general.placeholder.description',
        ),
        rows: 4,
      },
    },
    {
      propertyName: 'workspace_id',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.workspace',
      ),
      controlType: 'select',
      valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
        map((workspaces) =>
          workspaces.map((workspace) => ({
            value: workspace.id,
            name: workspace.title,
          })),
        ),
      ),
    },

    {
      propertyName: 'default_period_in_days',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.default-period-in-days',
      ),
      defaultValue: 7,
      controlType: 'input-number',
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
    },

    {
      propertyName: 'company_categories',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.categories',
      ),
      controlType: 'multiselect',
      valueOptionsAsync: this.availableCategories$.pipe(
        map((categories) =>
          categories.map((category) => ({
            value: category.company_category_id,
            name: category.company_category_name,
          })),
        ),
      ),
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
      availableInFormModes: [FormMode.Edit],
    },
    {
      propertyName: 'workflow_id',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.workflow',
      ),
      controlType: 'select',
      valueOptionsAsync: this.workflows$.pipe(
        map((workflows) =>
          workflows.map((workflow) => ({
            value: workflow.id,
            name: workflow.name,
          })),
        ),
      ),
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
      availableInFormModes: [FormMode.Edit],
    },
    {
      propertyName: 'cache_enabled',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.enable-caching',
      ),
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
      },
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
      availableInFormModes: [FormMode.Edit],
      defaultValue: true,
    },
    {
      propertyName: 'is_paused',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.is-paused',
      ),
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
      },
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
      availableInFormModes: [FormMode.Edit],
      defaultValue: true,
    },
    {
      propertyName: 'is_auto_refresh',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.is-auto-refresh-enabled',
      ),
      controlType: 'toggle',
      additionalConfig: {
        size: 'small',
      },
      shouldDisplay: () => {
        return this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION;
      },
      formChanger: true,
      availableInFormModes: [FormMode.Edit],
      defaultValue: false,
    },
    {
      propertyName: 'auto_refresh_seconds',
      propertyLabel: this.translateService.instant(
        'dashboard-settings.label.auto-refresh-seconds',
      ),
      controlType: 'input-number',
      shouldDisplay: (formGroup) => {
        return formGroup.get('is_auto_refresh')?.value;
      },
      additionalConfig: {
        min: 15,
      },
    },
    {
      propertyName: 'default_color',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.color',
      ),
      controlType: 'colorpicker',
      availableInFormModes: [FormMode.Edit],
    },
    {
      // wire key FeedPATCH binds as $imageUrl (base64 -> ClientImageStorage);
      // also keys the defaultImageUrl mapping in ngOnInit
      propertyName: 'image_url',
      propertyLabel: this.translateService.instant(
        'query-editor-general.label.image',
      ),
      controlType: 'image-cropper',
      availableInFormModes: [FormMode.Edit],
      additionalConfig: {
        height: 150,
        defaultImageUrl: '/assets/img/feed.png',
        containerStyleProperties: {
          width: '14.285rem',
        },
      },
    },
  ];

  ngOnInit(): void {
    if (this.feed) {
      this.formMode = FormMode.Edit;
    }

    if (this.feed) {
      this.formMode = FormMode.Edit;
      this.categoriesFeedId.set(this.feed.id);

      this.formProperties = this.formProperties.map((prop) => {
        if (prop.propertyName !== 'image_url') return prop;

        const defaultImageUrl =
          this.feed.thumbnail?.length > 0
            ? this.feed.thumbnail
            : '/assets/img/feed.png';

        return {
          ...prop,
          additionalConfig: {
            ...prop.additionalConfig,
            defaultImageUrl,
          },
        };
      });

      this.availableCategories$
        .pipe(
          tap((categories) => {
            this.availableCategories = categories;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    super.ngOnInit();
    // formGroup is now initialized

    if (this.feed) {
      const feedData = {
        ...this.feed,
        company_categories: this.feed.company_categories.map(
          (category) => category.company_category_id,
        ),
      };

      if (feedData['title_i18n']) {
        for (const [langId, title] of Object.entries(feedData['title_i18n'])) {
          feedData[`title_i18n.language_id_${langId}`] = title;
        }
      }

      this.formService.populateFormValues(this.formGroup, feedData);
    } else {
      this.store
        .select(selectActiveWorkspaceId)
        .pipe(
          tap((workspaceId) => {
            this.formService.populateFormValues(this.formGroup, {
              workspace_id: workspaceId,
            });
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    // * Subscribe to title field changes and emit them
    if (this.formGroup && this.formGroup.get('title')) {
      this.formGroup
        .get('title')
        .valueChanges.pipe(
          tap((title) => {
            this.componentDataChange.emit({ type: 'title', value: title });
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.setupWidgetPresetGallery();
  }

  ngAfterViewInit(): void {
    // When the wizard lands on step 3 the title is the first thing a user
    // wants to type, so focus it as soon as the dynamic-form children render.
    // queueMicrotask defers past the same-tick CD pass so the input is in the
    // DOM even when activeFormProperties was set synchronously above.
    queueMicrotask(() => this.focusTitleInput());
  }

  private focusTitleInput(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const titleInput = host.querySelector<HTMLInputElement>(
      'input[formcontrolname="title"]',
    );
    titleInput?.focus();
  }

  private setupWidgetPresetGallery(): void {
    if (this.isWidgetGalleryHidden()) return;

    this.formGroup.addControl(
      QueryEditorGeneralSettingsComponent.WIDGET_PRESETS_CONTROL,
      new FormControl<WidgetSelection[]>([]),
    );

    this.filteredWidgetPresets$ = combineLatest([
      this.widgetPresets$,
      this.widgetPresetSearch$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        startWith(''),
      ),
    ]).pipe(
      map(([presets, search]) => {
        const term = search.trim().toLowerCase();
        const filtered = !term
          ? presets
          : presets.filter(
              (p) =>
                p.name?.toLowerCase().includes(term) ||
                p.description?.toLowerCase().includes(term),
            );
        // Sort a copy — store arrays are frozen by NgRx in dev.
        return [...filtered].sort(
          (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
        );
      }),
    );

    this.presetsScope.set({
      objectTypeId: this.objectTypeId,
      objectClassId: NomObjectClass.FEED,
    });

    // Seed the gallery with the first 2 presets (by display_order) the first
    // time they arrive, so the user sees a sensible default without having to
    // click anything. Skipped if they've already made a choice.
    this.widgetPresets$
      .pipe(
        filter((presets) => presets?.length > 0),
        take(1),
        tap((presets) => this.applyDefaultSelections(presets)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private applyDefaultSelections(presets: WidgetPreset[]): void {
    if (this.selectedWidgetSelections().length > 0) return;

    const defaults: WidgetSelection[] = [...presets]
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .slice(0, 2)
      .map((preset) => ({
        preset_id: preset.id,
        viz_type_ids: preset.chart_types?.[0] ? [preset.chart_types[0].id] : [],
      }));

    this.setWidgetSelections(defaults);
  }

  onWidgetSearchChange(value: string): void {
    this.widgetPresetSearch$.next(value ?? '');
  }

  // The gallery is a create-time aid — hide it in edit mode and for object
  // types that don't represent a user-facing dashboardable feed.
  isWidgetGalleryHidden(): boolean {
    return (
      this.formMode === FormMode.Edit ||
      this.objectTypeId === NomObjectTypes.SEARCH_DIMENSION
    );
  }

  selectedWidgetSelections(): WidgetSelection[] {
    const control = this.formGroup?.get(
      QueryEditorGeneralSettingsComponent.WIDGET_PRESETS_CONTROL,
    );
    return (control?.value as WidgetSelection[]) ?? [];
  }

  isPresetSelected(presetId: number): boolean {
    return this.selectedWidgetSelections().some(
      (s) => s.preset_id === presetId,
    );
  }

  selectedVizTypeIdsFor(presetId: number): number[] {
    return (
      this.selectedWidgetSelections().find((s) => s.preset_id === presetId)
        ?.viz_type_ids ?? []
    );
  }

  selectedWidgetsCount(): number {
    return this.selectedWidgetSelections().reduce(
      (total, selection) => total + selection.viz_type_ids.length,
      0,
    );
  }

  // Click on the preset card body — toggle the whole preset on/off. When
  // turning on, auto-select the first viz type so the selection is never
  // ambiguous; turning off discards all viz selections for the preset.
  togglePreset(preset: WidgetPreset): void {
    const current = this.selectedWidgetSelections();
    const existing = current.find((s) => s.preset_id === preset.id);

    const next = existing
      ? current.filter((s) => s.preset_id !== preset.id)
      : [
          ...current,
          {
            preset_id: preset.id,
            viz_type_ids: preset.chart_types?.[0]
              ? [preset.chart_types[0].id]
              : [],
          },
        ];

    this.setWidgetSelections(next);
  }

  // Click on an individual viz type inside a card. If the preset isn't yet
  // selected, this both selects the preset and picks just this viz type. If
  // toggling off leaves the preset with zero viz types, drop the preset
  // entry entirely so `isPresetSelected` stays consistent.
  toggleVizType(payload: {
    widgetPreset: WidgetPreset;
    vizType: WidgetVizType;
  }): void {
    const { widgetPreset: preset, vizType } = payload;
    const current = this.selectedWidgetSelections();
    const existing = current.find((s) => s.preset_id === preset.id);

    if (!existing) {
      this.setWidgetSelections([
        ...current,
        { preset_id: preset.id, viz_type_ids: [vizType.id] },
      ]);
      return;
    }

    const hasViz = existing.viz_type_ids.includes(vizType.id);
    const nextVizIds = hasViz
      ? existing.viz_type_ids.filter((id) => id !== vizType.id)
      : [...existing.viz_type_ids, vizType.id];

    if (nextVizIds.length === 0) {
      this.setWidgetSelections(
        current.filter((s) => s.preset_id !== preset.id),
      );
      return;
    }

    this.setWidgetSelections(
      current.map((s) =>
        s.preset_id === preset.id ? { ...s, viz_type_ids: nextVizIds } : s,
      ),
    );
  }

  private setWidgetSelections(value: WidgetSelection[]): void {
    this.formGroup
      .get(QueryEditorGeneralSettingsComponent.WIDGET_PRESETS_CONTROL)
      ?.setValue(value);
  }

  trackPresetById(_index: number, preset: WidgetPreset): number {
    return preset.id;
  }
}
